import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'

import { dateFnsLocale } from '@/utils/dateLocale'
import { useNow } from '@/hooks/useNow'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { getLeadPendingCall, setLeadCallResult } from '@/api/inspection-leads'
import { callAttemptLabel, callResultLabel } from '@/utils/callResult'
import {
  CALL_ATTEMPTS_FINAL,
  CALL_RESULT_REMIND_MS,
  CALL_RESULTS_NOT_REACHED,
  CALL_RESULTS_REACHED,
  type CallResult,
  type InspectionLeadRow,
} from '@/types/inspection-lead'

/**
 * Результат звонка ФО в палитре (блок «Связь с клиентом»): текущий результат
 * и «Как прошёл звонок?» по последнему отвеченному звонку Mango без
 * результата (inspection_leads.pending_call_id). Без такого звонка выбора
 * нет: звонки мимо Mango не учитываются (решение 04.11). «Не дозвонились»
 * ставит БД сама. Через 30 минут без результата рамка блока оранжевая.
 */
export function LeadCallResultControl({
  lead,
  flushDraft,
}: {
  lead: InspectionLeadRow
  flushDraft: () => Promise<void>
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [pick, setPick] = useState<CallResult | ''>('')
  const [note, setNote] = useState('')
  const [callbackAt, setCallbackAt] = useState('')

  // Отказ, стоп-фактор и «перезвонить» требуют ещё одного шага; остальное сразу.
  const needsStep = (r: CallResult) => r === 'callback' || r === 'refused' || r === 'stop_factor'

  const mut = useMutation({
    mutationFn: async (r: CallResult) => {
      // Адрес / квалификация могли быть только что введены слева.
      await flushDraft()
      await setLeadCallResult(lead.id, r, {
        callId: pending?.id ?? null,
        note: note.trim() || null,
        callbackAt: r === 'callback' && callbackAt ? new Date(callbackAt).toISOString() : null,
      })
    },
    onSuccess: () => {
      setPick('')
      setNote('')
      setCallbackAt('')
      void qc.invalidateQueries({ queryKey: ['inspection-lead', lead.id] })
      void qc.invalidateQueries({ queryKey: ['lead-activity', lead.id] })
      void qc.invalidateQueries({ queryKey: ['lead-call-results', lead.id] })
      void qc.invalidateQueries({ queryKey: ['lead-pending-call', lead.id] })
      void qc.invalidateQueries({ queryKey: ['inspection-leads'] })
      void qc.invalidateQueries({ queryKey: ['lead-stage-counts'] })
    },
    // Сразу применяемый результат (например, «передано в ОО» без адреса):
    // сбрасываем выбор, чтобы после правки полей его можно было выбрать снова.
    onError: (_e, r) => {
      if (!needsStep(r)) setPick('')
    },
  })

  const pendingQ = useQuery({
    queryKey: ['lead-pending-call', lead.id],
    queryFn: () => getLeadPendingCall(lead.id),
    enabled: lead.status === 'in_progress',
    refetchInterval: 10_000,
  })
  const pending = lead.status === 'in_progress' && !!lead.assigned_to ? pendingQ.data ?? null : null
  const canPick = !!pending
  const now = useNow()
  const remind = !!pending && now - new Date(pending.started_at).getTime() > CALL_RESULT_REMIND_MS

  const errorText = (() => {
    if (!mut.isError) return null
    const msg = (mut.error as Error).message
    const m = msg.match(/missing_fields:([a-z_,]+)/)
    if (m) {
      const fields = m[1].split(',').map((f) => t(`leads.callResult.field.${f}`)).join(', ')
      return t('leads.callResult.missing', { fields })
    }
    return msg
  })()

  const muted = 'text-xs text-[color:var(--stryzh-color-text-secondary)]'
  const fmt = (iso: string) => format(new Date(iso), 'd MMM, HH:mm', { locale: dateFnsLocale() })
  const fmtDuration = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`

  return (
    <div className="flex flex-col gap-2">
      {lead.call_result ? (
        <div className="rounded-lg border border-[var(--stryzh-color-border-default)] px-3 py-2">
          {/* Это итог по лиду, а не запись о звонке: дата рядом это момент,
              когда результат поставили, поэтому «поставлен». */}
          <div className={muted}>
            {t('leads.callResult.title')}
            {lead.call_result_at ? ` · ${t('leads.callResult.setAt', { at: fmt(lead.call_result_at) })}` : ''}
            {lead.call_result_attempt != null ? ` · ${t('leads.callResult.auto')}` : ''}
          </div>
          <div className="text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {callResultLabel(lead.call_result, t)}
          </div>
          {lead.call_result_attempt != null ? (
            <>
              <div className="mt-1.5 flex gap-1">
                {Array.from({ length: CALL_ATTEMPTS_FINAL }, (_, i) => (
                  <span
                    key={i}
                    className={
                      'h-1 flex-1 rounded-full ' +
                      (i < lead.call_result_attempt! ? 'bg-[var(--stryzh-color-accent-orange)]' : 'bg-[var(--stryzh-color-border-default)]')
                    }
                  />
                ))}
              </div>
              <div className={'mt-1 ' + muted}>{callAttemptLabel(lead.call_result_attempt, t)}</div>
            </>
          ) : null}
          {lead.call_result === 'callback' && lead.callback_at ? (
            <div className={'mt-1 ' + muted}>{t('leads.callResult.callbackAt', { at: fmt(lead.callback_at) })}</div>
          ) : null}
        </div>
      ) : null}

      {canPick && pending ? (
        <div
          className={
            'flex flex-col gap-2 rounded-lg border px-3 py-2 ' +
            (remind ? 'border-[var(--stryzh-color-accent-orange)]' : 'border-[var(--stryzh-color-border-default)]')
          }
        >
          <div className={muted}>
            {t('leads.callResult.pendingCall', { at: fmt(pending.started_at), dur: fmtDuration(pending.duration_sec) })}
            {pending.manager_name ? ` · ${pending.manager_name}` : ''}
          </div>
          {pending.summary ? (
            <div className="text-xs text-[color:var(--stryzh-color-text-primary)]">{pending.summary}</div>
          ) : null}
          <div className="text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {t('leads.callResult.howWasIt')}
          </div>
          <Select<CallResult>
            value={pick}
            emptyLabel={t('leads.callResult.placeholder')}
            disabled={mut.isPending}
            onChange={(r) => {
              mut.reset()
              setPick(r)
              if (r && !needsStep(r)) mut.mutate(r)
            }}
            options={[...CALL_RESULTS_REACHED, ...CALL_RESULTS_NOT_REACHED].map((r) => ({
              value: r,
              label:
                callResultLabel(r, t) +
                (r === 'refused' || r === 'stop_factor' ? ` (${t('leads.callResult.willCancel')})` : ''),
            }))}
          />
          {pick === 'callback' ? (
            <>
              <Input
                type="datetime-local"
                label={t('leads.callResult.callbackLabel')}
                value={callbackAt}
                onChange={(e) => setCallbackAt(e.target.value)}
              />
              <Button type="button" variant="control" disabled={mut.isPending} onClick={() => mut.mutate('callback')}>
                {t('leads.callResult.save')}
              </Button>
            </>
          ) : null}
          {pick === 'refused' || pick === 'stop_factor' ? (
            <>
              <Input
                value={note}
                placeholder={t(pick === 'stop_factor' ? 'leads.callResult.notePh' : 'leads.callResult.refusedPh')}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                type="button"
                variant="danger"
                disabled={mut.isPending || (pick === 'stop_factor' && !note.trim())}
                onClick={() => mut.mutate(pick)}
              >
                {t('leads.callResult.cancelLead')}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {errorText ? <div className="text-xs text-red-300">{errorText}</div> : null}
    </div>
  )
}
